import React from "react";
import { Link } from "react-router-dom";
import { Lightbulb, Heart, Zap, Wallet, ArrowRight } from "lucide-react";
import { Section, Reveal, Eyebrow } from "@/components/primitives";

const values = [
  { icon: Lightbulb, title: "Cose semplici", desc: "Rendiamo facile quello che sembra difficile. Niente termini complicati." },
  { icon: Zap, title: "Parliamo chiaro", desc: "Ti spieghiamo tutto passo dopo passo, senza farti sentire a disagio." },
  { icon: Heart, title: "Vicini a te", desc: "Trattiamo la tua attività come fosse la nostra. Ci sei a cuore davvero." },
  { icon: Wallet, title: "Prezzi onesti", desc: "Costi chiari e alla portata di tutti. Mai una sorpresa sul conto." },
];

const numbers = [
  { value: "Gratis", label: "La prima bozza" },
  { value: "Pochi", label: "Giorni per il sito" },
  { value: "0", label: "Paroloni difficili" },
  { value: "5★", label: "Clienti contenti" },
];

export default function ChiSiamo() {
  return (
    <div data-testid="chi-siamo-page">
      <Section className="pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <Eyebrow>Chi siamo</Eyebrow>
            <h1 className="mt-5 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05]">
              Al fianco di chi vuole crescere online.
            </h1>
            <p className="mt-6 text-lg text-ink/70 leading-relaxed">
              MC Studio nasce con un'idea semplice: ogni azienda — dalla piccola
              attività di quartiere al libero professionista, fino alla media e
              grande impresa — merita una presenza online seria e curata.
            </p>
            <p className="mt-4 text-ink/70 leading-relaxed">
              Una presenza online strutturata oggi fa la differenza per crescere,
              a qualsiasi dimensione. Noi la rendiamo semplice: ti mostriamo una
              bozza gratis, ti spieghiamo tutto con parole chiare e ci occupiamo
              noi del resto. Tu pensi al tuo lavoro.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.12)]">
              <img
                src="https://images.unsplash.com/photo-1681949103006-70066fb25dfe?w=1000&q=80"
                alt="Il team di MC Studio al lavoro su progetti digitali"
                className="w-full h-[440px] object-cover"
              />
            </div>
          </Reveal>
        </div>
      </Section>

      {/* Mission dark */}
      <div className="bg-ink text-beige">
        <Section className="py-20">
          <Reveal>
            <div className="max-w-3xl">
              <Eyebrow dark>La nostra missione</Eyebrow>
              <p className="mt-6 font-display font-bold text-2xl lg:text-3xl tracking-tight leading-snug">
                Rendere il digitale di qualità <span className="text-coral">accessibile a ogni impresa</span>,
                con la cura di chi fa le cose a regola d'arte e la velocità di chi sa il fatto suo.
              </p>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
            {numbers.map((n, i) => (
              <Reveal key={n.label} delay={i * 0.08}>
                <div data-testid={`stat-${i}`} className="border-t border-beige/15 pt-5">
                  <div className="font-display font-black text-4xl text-coral">{n.value}</div>
                  <div className="mt-1 text-beige/60 text-sm">{n.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      {/* Values */}
      <Section className="py-24">
        <Reveal>
          <div className="max-w-2xl">
            <Eyebrow>I nostri valori</Eyebrow>
            <h2 className="mt-4 font-display font-bold text-3xl lg:text-4xl tracking-tight text-ink">
              Ciò in cui crediamo.
            </h2>
          </div>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div data-testid={`value-card-${i}`} className="bg-white rounded-2xl p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)] h-full">
                <div className="h-12 w-12 rounded-xl bg-coral/10 flex items-center justify-center text-coral">
                  <v.icon size={22} />
                </div>
                <h3 className="mt-5 font-semibold text-lg text-ink">{v.title}</h3>
                <p className="mt-2 text-ink/60 text-sm leading-relaxed">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Founder */}
      <Section className="pb-24">
        <Reveal>
          <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-[0_4px_24px_rgba(0,0,0,0.04)] grid md:grid-cols-3 gap-10 items-center">
            <img
              src="https://images.unsplash.com/photo-1713946598186-8e28275719b9?w=600&h=600&fit=crop&q=80"
              alt="Marco Conti, fondatore di MC Studio"
              className="w-full h-72 object-cover rounded-2xl md:col-span-1"
            />
            <div className="md:col-span-2">
              <Eyebrow>Il fondatore</Eyebrow>
              <h3 className="mt-3 font-display font-bold text-2xl text-ink">Marco Conti</h3>
              <p className="text-coral font-medium text-sm">Founder & Creative Director</p>
              <p className="mt-4 text-ink/70 leading-relaxed">
                "Ho aperto MC Studio perché vedevo tante realtà, piccole e
                grandi, che non sfruttavano davvero il web. Il mio lavoro è
                rendere tutto semplice: ti mostro com'è il tuo sito, gratis, e
                da lì partiamo. Senza fretta e senza paroloni."
              </p>
              <Link to="/contatti" data-testid="chisiamo-cta" className="mt-6 inline-flex items-center gap-2 text-ink font-semibold hover:text-coral transition-colors">
                Lavoriamo insieme <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
